import React, { useEffect, useState } from "react";
import { growthApi } from "../api";
import { Badge } from "./ui/badge";
import { TrendingUp, Megaphone, Users, AlertTriangle } from "lucide-react";

const ListCard = ({ title, icon: Icon, items, valueKey, accent }) => (
  <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
    <h3 className="font-display font-bold text-[#0E2A33] flex items-center gap-2 mb-4">
      <Icon className="h-5 w-5" style={{ color: accent }} /> {title}
    </h3>
    <div className="space-y-2">
      {items.map((c, i) => (
        <div key={c.id} className="flex items-center justify-between gap-2 rounded-lg border border-slate-100 px-3 py-2">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-xs font-bold w-5 text-slate-400">#{i + 1}</span>
            <span className="font-medium text-[#0E2A33] text-sm truncate">{c.name}</span>
            {c.providerCountDisplay === "UNKNOWN" && (
              <Badge className="border text-[10px] bg-slate-100 text-slate-500 border-slate-200">data needed</Badge>
            )}
          </div>
          <span className="text-sm font-semibold shrink-0" style={{ color: accent }}>{c[valueKey]}</span>
        </div>
      ))}
      {items.length === 0 && <div className="text-sm text-slate-400 py-4 text-center">No data yet</div>}
    </div>
  </div>
);

const GrowthPriorities = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    growthApi.priorities().then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return <div className="text-center text-slate-400 py-10">Computing weekly priorities…</div>;

  return (
    <div data-testid="growth-priorities" className="space-y-6">
      <p className="text-sm text-slate-500">
        Recalculated live from county data + internal conversions. The three lists are intentionally independent —
        a county can be a high SEO priority but a medium outreach priority.
      </p>
      <div className="grid lg:grid-cols-3 gap-6">
        <ListCard title="Top 10 SEO Counties" icon={TrendingUp} items={data.topSeo} valueKey="seoPriorityValue" accent="#0E5A72" />
        <ListCard title="Top 10 Referral Outreach" icon={Megaphone} items={data.topOutreach} valueKey="outreachPriorityValue" accent="#2E9E6A" />
        <ListCard title="Top 10 Caregiver Recruitment" icon={Users} items={data.topRecruiting} valueKey="recruitingPriorityValue" accent="#7C3AED" />
      </div>
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
        <h3 className="font-display font-bold text-[#0E2A33] flex items-center gap-2 mb-2">
          <AlertTriangle className="h-5 w-5 text-red-500" /> Recruitment Priorities (demand + staffing gap)
        </h3>
        <p className="text-sm text-slate-500 mb-4">
          Counties with verified referral demand (≥3) and low staffing capacity (≤3). Support these with /careers outreach —
          only advertise a role when an actual staffing need exists.
        </p>
        <div className="flex flex-wrap gap-2">
          {data.recruitmentPriorities.map((c) => (
            <Badge key={c.id} className="border bg-red-50 text-red-700 border-red-200 text-sm px-3 py-1.5">
              {c.name} — demand {c.doddReferralActivity ?? 0}, staffing {c.staffingCapacity}/10
            </Badge>
          ))}
          {data.recruitmentPriorities.length === 0 && (
            <span className="text-sm text-slate-400">None flagged yet. Enter DODD referral activity and staffing capacity in the County Engine to surface gaps.</span>
          )}
        </div>
      </div>
    </div>
  );
};

export default GrowthPriorities;
